import type { MaskedTile as Tile } from '@completto/shared';
import { html, View } from 'rune-ts';
import type { PoolFilterMode } from '../../stores/index.ts';
import { PoolChipView } from './PoolChip.ts';

export type OpenPoolData = {
  tiles: readonly Tile[];
  /** placement 가능한 타일 ID 집합. 이 집합 안의 칩만 시각적 ✨ + 클릭 가능. */
  placeableTileIds: ReadonlySet<string>;
  /** 내 차례 + holding 없음. false면 모든 칩 disabled. */
  enabled: boolean;
  filter: PoolFilterMode;
  onChipClick: (tileId: string) => void;
  onFilterChange: (mode: PoolFilterMode) => void;
};

export class OpenPoolView extends View<OpenPoolData> {
  override template() {
    const { tiles, placeableTileIds, enabled, filter, onChipClick } = this.data;
    if (tiles.length === 0) {
      return html`
        <div class="open-pool flex flex-col items-center justify-center gap-2 rounded-md bg-wood-paper/40 ring-1 ring-wood-sand/60 px-2 py-1 mlandscape:py-6 w-full mlandscape:min-h-[120px]">
          <div class="flex items-center gap-2 mlandscape:hidden">
            <span class="text-caption text-wood-coffee/70">풀</span>
            <span class="text-caption text-wood-coffee/55">비었음</span>
          </div>
          <div class="hidden mlandscape:flex flex-col items-center gap-1">
            <span class="text-subhead text-wood-coffee/75 font-semibold">공개 풀이 비었습니다</span>
            <span class="text-caption text-wood-coffee/60">아래 버튼으로 더미에서 뽑으세요</span>
          </div>
        </div>
      `;
    }
    // 활성(placeable) 칩을 비활성보다 앞에. placeable 그룹 내부는 number 오름차순으로
    // 정렬해 "내가 놓을 수 있는 가장 작은 수"를 빠르게 인지하도록.
    const placeable = tiles
      .filter((t) => placeableTileIds.has(t.id))
      .slice()
      .sort((a, b) => {
        const an = a.resolvedNumber ?? a.number ?? 0;
        const bn = b.resolvedNumber ?? b.number ?? 0;
        return an - bn;
      });
    const others = tiles.filter((t) => !placeableTileIds.has(t.id));
    const visible = filter === 'PLACEABLE_ONLY' ? placeable : [...placeable, ...others];
    const chips = visible.map((t) => {
      const isPlaceable = placeableTileIds.has(t.id);
      return new PoolChipView({
        tile: t,
        highlightForMe: isPlaceable,
        enabled: enabled && isPlaceable,
        onClick: onChipClick,
      });
    });
    const totalLabel =
      filter === 'PLACEABLE_ONLY' ? `${placeable.length}/${tiles.length}` : `${tiles.length}`;
    const showFilterToggle = placeable.length > 0 && tiles.length > placeable.length;
    return html`
      <div class="open-pool flex flex-col rounded-xl bg-wood-paper/70 ring-1 ring-wood-sand p-2 w-full min-h-[80px] mlandscape:min-h-0 mlandscape:max-h-full mlandscape:flex-1 mlandscape:overflow-hidden">
        <div class="flex items-center justify-between gap-2 px-1 mb-1">
          <span class="text-caption text-wood-coffee">공개 풀 (${totalLabel})</span>
          ${
            showFilterToggle
              ? html`<div
                  class="pool-filter-toggle flex items-center rounded-md bg-wood-cream ring-1 ring-wood-sand text-[10px] tabular-nums"
                  role="group"
                  aria-label="풀 필터"
                >
                  <button
                    type="button"
                    data-filter="ALL"
                    aria-pressed="${filter === 'ALL'}"
                    class="px-2 py-0.5 rounded-md transition ${
                      filter === 'ALL'
                        ? 'bg-card-navy text-card-gold font-semibold'
                        : 'text-wood-coffee/70 hover:text-wood-ink'
                    }"
                  >
                    전체
                  </button>
                  <button
                    type="button"
                    data-filter="PLACEABLE_ONLY"
                    aria-pressed="${filter === 'PLACEABLE_ONLY'}"
                    class="px-2 py-0.5 rounded-md transition ${
                      filter === 'PLACEABLE_ONLY'
                        ? 'bg-emerald-500 text-white font-semibold'
                        : 'text-wood-coffee/70 hover:text-wood-ink'
                    }"
                  >
                    가능한 것만
                  </button>
                </div>`
              : html``
          }
        </div>
        <div class="flex items-center gap-1.5 overflow-x-auto overflow-y-visible px-1 pt-2 pb-1 min-h-[56px] sm:min-h-[64px] mlandscape:flex-wrap mlandscape:items-start mlandscape:overflow-x-visible mlandscape:overflow-y-auto mlandscape:gap-y-2 mlandscape:min-h-0 mlandscape:flex-1">
          ${chips}
        </div>
      </div>
    `;
  }

  protected override onMount(): void {
    const root = this.element();
    root.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.filter as PoolFilterMode | undefined;
        if (mode === 'ALL' || mode === 'PLACEABLE_ONLY') {
          this.data.onFilterChange(mode);
        }
      });
    });
  }
}
